import React, { useEffect, useRef, useState } from "react";
import { captureException } from "@sentry/nextjs";
import { Tag } from "antd";
import { BICOLORS } from "@app/utils/constants/colorConstants";
import InfyButton from "../../../InfyButton/InfyButton";
import { submitLearnerResponse } from "../../apis";
import FormCompleted from "../FormCompleted";
import { V2FormData, V2Question } from "../../helper";

interface CourseFeedbackFormProps {
  formData: V2FormData;
  formId: string;
  courseId: string | null;
}

type RatingBucket = "low" | "medium" | "high";

const RATING_CONFIG: Record<RatingBucket, { emoji: string; label: string }> = {
  low: { emoji: "😞", label: "Not working for me" },
  medium: { emoji: "😌", label: "Decent, but could be better" },
  high: { emoji: "😄", label: "Really enjoyed it" }
};

const getRatingBucket = (rating: number): RatingBucket | null => {
  if (rating === 0) return null;
  if (rating <= 2) return "low";
  if (rating === 3) return "medium";
  return "high";
};

const formatTag = (tag: string): string =>
  tag
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/^\w/, c => c.toUpperCase());

const CourseFeedbackForm: React.FC<CourseFeedbackFormProps> = ({ formData, formId, courseId }) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [rating, setRating] = useState(5);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [textAnswer, setTextAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const settings = (formData?.settings as Record<string, unknown>) || {};
  const ratingTags = (settings.ratingTags as Record<RatingBucket, string[]>) || { low: [], medium: [], high: [] };
  const questions: V2Question[] = formData?.questions || [];

  const ratingQuestion = questions.find(q => q.questionType === "RATING");
  const tagsQuestion = questions.find(q => q.questionType === "MULTISELECT_TAGS");
  const longTextQuestion = questions.find(q => q.questionType === "LONG_TEXT");

  const bucket = getRatingBucket(rating);
  const currentTags: string[] = bucket ? ratingTags[bucket] || [] : [];
  const ratingConfig = bucket ? RATING_CONFIG[bucket] : null;

  useEffect(() => {
    setSelectedTags([]);
  }, [bucket]);

  const toggleTag = (tag: string) => {
    setSelectedTags(prev => (prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]));
  };

  const handleMaybeLater = () => {
    window?.parent?.postMessage({ action: "formSkipped" }, "*");
  };

  const handleSubmit = async () => {
    if (!rating || submitting) return;
    setSubmitting(true);
    try {
      const responses: { questionId: string; answer: string | number | string[] | null }[] = [];

      if (ratingQuestion) {
        responses.push({ questionId: ratingQuestion.id, answer: rating });
      }
      if (tagsQuestion) {
        responses.push({ questionId: tagsQuestion.id, answer: selectedTags.length > 0 ? selectedTags : null });
      }
      if (longTextQuestion) {
        responses.push({ questionId: longTextQuestion.id, answer: textAnswer.trim() || null });
      }

      await submitLearnerResponse(formId, { courseId, responses });

      setSubmitted(true);
      window?.parent?.postMessage({ action: "formSubmitted" }, "*");
    } catch (err) {
      captureException(err);
      window?.parent?.postMessage({ action: "somethingWentWrong" }, "*");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) return <FormCompleted />;

  const displayRating = hoveredRating || rating;

  useEffect(() => {
    const sendHeight = () => {
      const height = rootRef.current?.getBoundingClientRect().height;
      if (height) window.parent.postMessage({ type: "iframeResize", height }, "*");
    };

    sendHeight();
    const observer = new ResizeObserver(sendHeight);
    if (rootRef.current) observer.observe(rootRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={rootRef} className="flex flex-col bg-neutral-1">
      {/* Stars */}
      <div className="flex gap-3 mb-6 justify-center">
        {[1, 2, 3, 4, 5].map(star => {
          const filled = star <= displayRating;
          return (
            <button
              key={star}
              type="button"
              aria-label={`Rate ${star} star${star > 1 ? "s" : ""}`}
              onClick={() => setRating(star)}
              onMouseEnter={() => setHoveredRating(star)}
              onMouseLeave={() => setHoveredRating(0)}
              className="focus:outline-none transition-transform hover:scale-110"
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill={filled ? "#F5A623" : "none"} stroke={filled ? "#F5A623" : "#D1D5DB"} strokeWidth="1.5">
                <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
              </svg>
            </button>
          );
        })}
      </div>

      {/* Emoji + label */}
      {ratingConfig && (
        <div className="flex items-center gap-2 mb-5">
          {/* Replace span with <img src="/path/to/emoji.svg" /> when asset is ready */}
          <span className="text-2xl leading-none">{ratingConfig.emoji}</span>
          <span className="text-base font-semibold text-gray-800">{ratingConfig.label}</span>
        </div>
      )}

      {/* Tag pills */}
      {currentTags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {currentTags.map(tag => {
            const isSelected = selectedTags.includes(tag);
            return (
              <Tag.CheckableTag
                key={tag}
                checked={isSelected}
                onChange={() => toggleTag(tag)}
                style={{
                  border: `1px solid ${isSelected ? BICOLORS.primary[6] : BICOLORS.neutral[6]}`,
                  borderRadius: 6,
                  color: isSelected ? BICOLORS.primary[6] : BICOLORS.neutral[12],
                  background: isSelected ? BICOLORS.primary[1] : BICOLORS.neutral[4],
                  padding: "6px 8px",
                  fontSize: 12,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  margin: 0
                }}
              >
                {isSelected && (
                  // Replace with <img src="/icons/check.svg" /> when asset is ready
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
                {formatTag(tag)}
              </Tag.CheckableTag>
            );
          })}
        </div>
      )}

      {/* Long text */}
      {longTextQuestion && rating > 0 && (
        <div className="mb-6">
          <p className="text-base font-semibold text-gray-800 mb-3">{longTextQuestion.label}</p>
          <div className="relative">
            <textarea
              value={textAnswer}
              onChange={e => setTextAnswer(e.target.value)}
              placeholder={longTextQuestion.placeholder || "Share more details here (Optional)"}
              rows={5}
              className="w-full border border-gray-200 rounded-lg px-4 py-3 pr-10 text-sm text-gray-700 placeholder-gray-400 resize-none focus:outline-none focus:border-gray-300"
            />
            {textAnswer && (
              <button
                type="button"
                aria-label="Clear text"
                onClick={() => setTextAnswer("")}
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-end gap-4">
        <InfyButton type="ghost" bicolor="primary" onClick={handleMaybeLater}>
          Maybe later
        </InfyButton>
        <InfyButton type="primary" bicolor="primary" loading={submitting} disabled={!rating} onClick={handleSubmit}>
          Submit Review
        </InfyButton>
      </div>
    </div>
  );
};

export default CourseFeedbackForm;
